import { Directive, ElementRef, HostListener, inject, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { debounceTime, fromEvent, startWith } from 'rxjs';

/**
 * A directive that adjusts an element's position based on page scroll, useful for creating sticky elements
 * that need to account for a fixed header or other elements.
 * 
 * @example
 * <!-- Basic usage with default threshold -->
 * <div rxApplicationsPageScroll></div>
 * 
 * <!-- With custom scroll threshold -->
 * <div rxApplicationsPageScroll [scrollThreshold]="100"></div>
 * 
 * @param scrollThreshold - The number of pixels to scroll before the element starts adjusting its position.
 *                         This value should match the height of any fixed elements (like headers) that the
 *                         sticky element should account for.
 */
@Directive({
  standalone: true,
  selector: '[rxApplicationsPageScroll]',
})
export class PageScrollDirective implements OnInit, OnDestroy {
  #element = inject(ElementRef);
  #renderer = inject(Renderer2);

  @Input() scrollThreshold = 81.5; // 81.5 pixels is approximately the main navigation header height
  #scrollSubscription;

  ngOnInit(): void {
    this.#scrollSubscription = fromEvent(window, 'scroll')
      .pipe(
        startWith(null), // Executes at least once in case the user scrolls before the directive is initialized
        debounceTime(150),
      )
      .subscribe(() => this.onScroll());
  }

  ngOnDestroy(): void {
    if (this.#scrollSubscription) {
      this.#scrollSubscription.unsubscribe();
    }
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    const scrollY = window.scrollY;
    const scrollDistance = Math.min(this.scrollThreshold, scrollY);
    this.#renderer.setStyle(this.#element.nativeElement, 'top', `${-scrollDistance + this.scrollThreshold}px`);
  }
}
